import React, { useRef } from 'react';
import { observer } from "mobx-react-lite";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import styled from 'styled-components';
import ChatBoxPersistent from "./ChatBoxPersistent";
import ChatBoxPersistentHTTP from "./ChatBoxPersistentHTTP";
import { ChatBoxWrapper, ChatContainer } from './ChatComponents.styled';
import { useChatContext } from '../../context/ChatContext';
import { Typography } from "@ann/web-components";
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';

const minimalTheme = createTheme({
  palette: {
    background: {
      paper: '#ffffff',
    },
  },
});

const ChatStartersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr; /* Por defecto 1 columna (laptop/tablet) */
  gap: 8px;
  margin-top: 16px;
  padding-inline-start: 16px;
  padding-inline-end: 16px;
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr; /* 2 columnas en desktop */
  }

  /* Asegurar que cada starter ocupe el 100% del ancho disponible */
  > div {
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
  }
`;

const ChatComponentPersistent = observer(({ 
  store, 
  customerSelected,
  agentId,
  agentDomain,
  inputWidth = "80vh",
  metadata = {},
  initialMessage = null,
  handleMetadata,
  agentData,
  children,
  initialContent,
  // Nuevas props para personalizar el placeholder
  placeholderTitle = "Test your Agent",
  placeholderDescription = "Use this simulated chat to see how your agent responds\nSend a message to preview its behavior.",
  placeholderIcon = ChatBubbleOutlineRoundedIcon,
  showDefaultPlaceholder = false,
  chatStarters = [],
  onChatStarterClick
}) => {
  const chatContext = useChatContext();
  const conversation = chatContext.getConversation(agentId);
  const hasMessages = conversation.hasMessages;
  const { communicationConfig } = chatContext;
  const chatRef = useRef(null);

  // Crear el placeholder por defecto si se solicita
  const defaultPlaceholder = showDefaultPlaceholder ? (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: '20px',
      gap: '16px'
    }}>
      {React.createElement(placeholderIcon, { 
        color: "primary", 
        style: { fontSize: '33.6px' } // 48px - 30% = 33.6px
      })}
      <Typography
        category="title"
        size="medium"
        weight="bold"
      >
        {placeholderTitle}
      </Typography>
      <Typography
        category="body"
        size="large"
        weight="regular"
        style={{ maxWidth: '200px' }}
      >
        {placeholderDescription.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < placeholderDescription.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </Typography>
      
      {/* Chat Starters Section */}
      {chatStarters && chatStarters.length > 0 && (
        <ChatStartersGrid>
          {chatStarters.map((starter, index) => (
            <div
              key={index}
              style={{
                width: '100%',
                height: '44px',
                borderRadius: '20px',
                border: '1px solid #e5e7eb',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: '#ffffff',
                transition: 'all 0.2s ease',
                opacity: 1,
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}
              onClick={() => {
                if (onChatStarterClick) {
                  onChatStarterClick(starter);
                }
                // Enviar el mensaje al chat directamente
                if (chatRef.current && chatRef.current.sendMessage) {
                  chatRef.current.sendMessage(starter);
                }
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.borderColor = '#d1d5db';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.borderColor = '#e5e7eb';
              }}
            >
              <Typography
                category="body"
                size="medium"
                weight="regular"
                style={{
                  color: '#374151',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  textAlign: 'center'
                }}
              >
                {starter}
              </Typography>
            </div>
          ))}
        </ChatStartersGrid>
      )}
    </div>
  ) : null;

  const chatProps = {
    customerSelected,
    agentId,
    agentDomain: agentDomain || false,
    inputWidth: inputWidth,
    metadata: metadata,
    initialMessage: initialMessage,
    handleMetadata: handleMetadata,
    agentData,
  };

  // Determinar qué componente de chat usar basándose en la configuración
  const ChatComponent = communicationConfig.type === 'http' ? ChatBoxPersistentHTTP : ChatBoxPersistent;

  return (
    <ChatContainer>
      <ThemeProvider theme={minimalTheme}>
        <ChatBoxWrapper>
          {!hasMessages && (children || initialContent || defaultPlaceholder) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: (initialContent || defaultPlaceholder) ? '400px' : '200px', // Más espacio para el ChatInput
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              pointerEvents: 'auto',
            }}>
              {children || initialContent || defaultPlaceholder}
            </div>
          )}
          <ChatComponent
            ref={chatRef}
            {...chatProps}
          />
        </ChatBoxWrapper>
      </ThemeProvider>
    </ChatContainer>
  );

});

export default ChatComponentPersistent; 